import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/config';
import { getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';

export type GithubConnection = {
  id: string;
  userId: string;
  repoOwner: string;
  repoName: string;
  branch: string;
  createdAt: string;
  updatedAt: string;
};

export type GithubRepo = {
  fullName: string;
  owner: string;
  name: string;
  defaultBranch: string;
  isPrivate: boolean;
};

function mapConnection(row: Record<string, unknown>): GithubConnection {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    repoOwner: row.repo_owner as string,
    repoName: row.repo_name as string,
    branch: row.branch as string,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

async function invokeGithubFunction(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { data: { session } } = await supabase.auth.getSession();

  const res = await fetch(`${SUPABASE_URL}/functions/v1/github-oauth`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token ?? ''}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ ...body, userId: session?.user?.id }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `GitHub function error ${res.status}`);
  }

  return res.json();
}

const githubService = {
  async getConnection(): Promise<GithubConnection | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const orgId = getActiveOrgId();
    let query = supabase
      .from('github_connections')
      .select('id, user_id, repo_owner, repo_name, branch, created_at, updated_at')
      .eq('user_id', user.id);

    if (orgId) query = query.eq('organization_id', orgId);

    const { data, error } = await query.maybeSingle();

    if (error) throw error;
    return data ? mapConnection(data) : null;
  },

  async startOAuthFlow(redirectPath = '/settings/integrations'): Promise<void> {
    const result = await invokeGithubFunction({ action: 'getClientId' });
    const clientId = result.clientId as string;

    const redirectUri = `${window.location.origin}${redirectPath}`;
    const state = crypto.randomUUID();
    sessionStorage.setItem('github_oauth_state', state);

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      scope: 'repo',
      state,
    });

    window.location.href = `https://github.com/login/oauth/authorize?${params}`;
  },

  async handleOAuthCallback(code: string, state: string): Promise<string> {
    const savedState = sessionStorage.getItem('github_oauth_state');
    if (state !== savedState) {
      throw new Error('OAuth state mismatch');
    }
    sessionStorage.removeItem('github_oauth_state');

    const result = await invokeGithubFunction({ action: 'exchangeCode', code });
    return result.accessToken as string;
  },

  async fetchRepos(accessToken: string): Promise<GithubRepo[]> {
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/vnd.github.v3+json',
    };

    const allRepos: GithubRepo[] = [];
    let page = 1;

    // Paginate through all accessible repos
    while (page <= 10) {
      const res = await fetch(
        `https://api.github.com/user/repos?per_page=100&sort=updated&type=all&page=${page}`,
        { headers }
      );

      if (!res.ok) {
        throw new Error('Failed to fetch repos from GitHub');
      }

      const repos = await res.json();
      if (repos.length === 0) break;

      allRepos.push(
        ...repos.map((r: Record<string, unknown>) => ({
          fullName: r.full_name as string,
          owner: (r.owner as Record<string, unknown>).login as string,
          name: r.name as string,
          defaultBranch: r.default_branch as string,
          isPrivate: r.private as boolean,
        }))
      );

      if (repos.length < 100) break;
      page += 1;
    }

    return allRepos;
  },

  async saveConnection(params: {
    accessToken: string;
    repoOwner: string;
    repoName: string;
    branch: string;
  }): Promise<GithubConnection> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const orgId = getActiveOrgId();
    const { data, error } = await supabase
      .from('github_connections')
      .upsert(
        {
          user_id: user.id,
          access_token: params.accessToken,
          repo_owner: params.repoOwner,
          repo_name: params.repoName,
          branch: params.branch,
          updated_at: new Date().toISOString(),
          ...(orgId ? { organization_id: orgId } : {}),
        },
        { onConflict: 'user_id' }
      )
      .select('id, user_id, repo_owner, repo_name, branch, created_at, updated_at')
      .single();

    if (error) throw error;
    return mapConnection(data);
  },

  async validateToken(token: string): Promise<boolean> {
    const res = await fetch('https://api.github.com/user', {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/vnd.github.v3+json',
      },
    });
    return res.ok;
  },

  async disconnect(): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('github_connections')
      .delete()
      .eq('user_id', user.id);

    if (error) throw error;
  },
};

export default githubService;
